import ReactECharts from 'echarts-for-react';

/**
 * CONTENT ALTERNATIVE (_4) to CostGapChart: per-function cost comparison.
 *
 * Instead of the waterfall (total $1.185M → $24K), this pairs fully-staffed
 * vs Raisy annual cost for EACH of the 5 business functions, so the reader
 * sees the gap is not an average artifact — every single function is tens of
 * times cheaper (~29×–71×, derived below from the same numbers).
 *
 * Data: public/blog/raisy-20-role-cost-model.csv (the 5 function rows; the
 * CSV total row $1,185,000 / $24,000 matches the original chart's constants).
 *
 * The ~50× ratio is handled honestly with a LOG-scaled x-axis (stated on the
 * axis and in the caption) plus an explicit "Nx cheaper" label per row —
 * the ratio is shown, never hidden.
 *
 * Light editorial card: white bg, slate text, teal accent.
 */

const SLATE_TEXT = '#334155';
const SLATE_MUTED = '#64748B';
const TEAL = '#0AB3BF';
const SLATE_BAR = '#94A3B8';

// exact values from public/blog/raisy-20-role-cost-model.csv
const FUNCTIONS: { short: string; full: string; human: number; raisy: number }[] = [
  { short: 'Fundraising', full: 'Fundraising researcher + pitch writer + tracker', human: 220000, raisy: 3600 },
  { short: 'Sales', full: 'Sales BDR + email copywriter + LinkedIn SDR', human: 215000, raisy: 4800 },
  { short: 'Recruiting', full: 'Recruiting sourcer + screener + scheduler', human: 105000, raisy: 3600 },
  { short: 'Support', full: 'Support agent + auto-responder', human: 80000, raisy: 2400 },
  { short: 'Investor relations', full: 'IR manager + board pack assembler', human: 170000, raisy: 2400 },
];

// derived by plain division on the CSV values above — visible here, not invented
const ratio = (f: (typeof FUNCTIONS)[number]): number => f.human / f.raisy;

/**
 * ONE shared money formatter — used by the tooltip AND the axis AND the bar
 * labels so no two renderings of the same number can disagree.
 */
const formatMoney = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(a / 1e3).toFixed(a % 1e3 === 0 ? 0 : 1)}K`;
  return `$${a}`;
};

export const CostGapChart_4 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: '#FFFFFF',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#FFFFFF',
      borderColor: '#E2E8F0',
      textStyle: { color: SLATE_TEXT, fontSize: 13 },
      formatter: (params: { seriesName: string; dataIndex: number; value: number }[]) => {
        const f = FUNCTIONS[params[0].dataIndex];
        const lines = params.map(
          (p) => `${p.seriesName}: <b>${formatMoney(p.value)}</b>/yr`,
        );
        return `<b>${f.full}</b><br/>${lines.join('<br/>')}${'<br/>'}Raisy is <b>${ratio(f).toFixed(0)}× cheaper</b>`;
      },
    },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: SLATE_MUTED, fontSize: 12 },
      itemWidth: 14,
      itemHeight: 10,
    },
    grid: { left: 8, right: 96, top: 34, bottom: 44, containLabel: true },
    xAxis: {
      type: 'log',
      min: 1000,
      // log scale stated on the axis itself — gridlines are powers of 10
      name: 'annual cost (USD/yr, log scale — each gridline is 10× the previous)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: SLATE_MUTED, fontSize: 11 },
      axisLine: { lineStyle: { color: '#CBD5E1' } },
      axisLabel: {
        color: SLATE_MUTED,
        fontSize: 11,
        formatter: (v: number) => formatMoney(v),
      },
      splitLine: { lineStyle: { color: '#E2E8F0' } },
    },
    yAxis: {
      type: 'category',
      data: FUNCTIONS.map((f) => f.short),
      axisLine: { lineStyle: { color: '#CBD5E1' } },
      axisTick: { show: false },
      axisLabel: { color: SLATE_TEXT, fontSize: 12, fontWeight: 600 },
    },
    series: [
      {
        name: 'Fully staffed',
        type: 'bar',
        barGap: '15%',
        barWidth: 12,
        itemStyle: { color: SLATE_BAR, borderRadius: [0, 4, 4, 0] },
        data: FUNCTIONS.map((f) => f.human),
        label: {
          show: true,
          position: 'right',
          color: SLATE_TEXT,
          fontSize: 11,
          fontWeight: 600,
          formatter: (p: { dataIndex: number }) => formatMoney(FUNCTIONS[p.dataIndex].human),
        },
      },
      {
        name: 'Raisy',
        type: 'bar',
        barWidth: 12,
        itemStyle: { color: TEAL, borderRadius: [0, 4, 4, 0] },
        data: FUNCTIONS.map((f) => f.raisy),
        label: {
          show: true,
          position: 'right',
          color: SLATE_MUTED,
          fontSize: 11,
          formatter: (p: { dataIndex: number }) => {
            const f = FUNCTIONS[p.dataIndex];
            return `${formatMoney(f.raisy)} · ${ratio(f).toFixed(0)}× cheaper`;
          },
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-4">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The ~50× gap holds function by function, not just in the $1.185M → $24K total — every one of the five
        functions costs tens of times less with Raisy (29× to 71×, labels per row). Log scale: each gridline is 10× the
        previous. Hover for exact values.
      </p>
    </div>
  );
};
